import {
  Paper,
  Title,
  Button,
  Text,
  Stack,
  Group,
  Table,
  ScrollArea,
  Badge,
  LoadingOverlay,
  Alert,
  Tabs
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCalendarStats, IconPlaylist, IconAlertCircle, IconBuilding, IconUsers } from '@tabler/icons-react';
import { useState } from 'react';
import type { Assignment } from '@shift-demo/shared';
import { useAppStore } from '../stores/useAppStore';

export function ShiftSchedule() {
  const [activeTab, setActiveTab] = useState<string>('line');
  const {
    employees,
    assignments,
    setAssignments,
    targetYear,
    targetMonth,
    isGeneratingSchedule,
    setIsGeneratingSchedule,
    error,
    setError,
    clearError,
    canGenerateSchedule
  } = useAppStore();

  const generateSchedule = async () => {
    if (!canGenerateSchedule()) {
      return;
    }
    
    setIsGeneratingSchedule(true);
    clearError();
    
    try {
      // TODO: 実際のAPI呼び出しを実装
      // const response = await fetch('/api/schedule', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({
      //     year: targetYear,
      //     month: targetMonth,
      //     employees
      //   })
      // });
      
      // モックデータ（選択された年月に基づく）
      // targetMonth は1ベース（1-12）なので、JavaScriptの0ベース月に変換
      const daysInMonth = new Date(targetYear, targetMonth, 0).getDate();
      
      // 多能工を取得（責任者候補）
      const multiSkilledEmployees = employees.filter(emp => emp.skills.length > 1);
      
      const mockAssignments: Assignment[] = [];
      
      // 1日から月末まで順番に生成
      for (let day = 1; day <= daysInMonth; day++) {
        // 日勤シフト
        const dayShiftDate = new Date(targetYear, targetMonth - 1, day);
        const dayShiftDateString = dayShiftDate.toISOString().split('T')[0];
        
        // 遅番シフト
        const lateShiftDate = new Date(targetYear, targetMonth - 1, day);
        const lateShiftDateString = lateShiftDate.toISOString().split('T')[0];
        
        // 日勤の責任者選出（日によって変える）
        const daySupIndex = (day - 1) % multiSkilledEmployees.length;
        const daySupervisor = multiSkilledEmployees[daySupIndex];
        
        // 遅番の責任者選出（日勤とは別の人）
        const lateSupIndex = (day - 1 + Math.floor(multiSkilledEmployees.length / 2)) % multiSkilledEmployees.length;
        const lateSupervisor = multiSkilledEmployees[lateSupIndex];
        
        // 日勤シフト追加
        mockAssignments.push({
          date: dayShiftDateString,
          shift: 'DAY',
          supervisorId: daySupervisor.id,
          lines: {
            A: `emp-${((day - 1) * 2) % 25 + 1}`,
            B: `emp-${((day - 1) * 2 + 5) % 25 + 1}`,
            C: `emp-${((day - 1) * 2 + 10) % 25 + 1}`,
            D: `emp-${((day - 1) * 2 + 15) % 25 + 1}`,
            E: `emp-${((day - 1) * 2 + 20) % 25 + 1}`
          }
        });
        
        // 遅番シフト追加
        mockAssignments.push({
          date: lateShiftDateString,
          shift: 'LATE',
          supervisorId: lateSupervisor.id,
          lines: {
            A: `emp-${((day - 1) * 2 + 1) % 25 + 1}`,
            B: `emp-${((day - 1) * 2 + 6) % 25 + 1}`,
            C: `emp-${((day - 1) * 2 + 11) % 25 + 1}`,
            D: `emp-${((day - 1) * 2 + 16) % 25 + 1}`,
            E: `emp-${((day - 1) * 2 + 21) % 25 + 1}`
          }
        });
      }
      
      setAssignments(mockAssignments);
      
      notifications.show({
        title: 'シフト生成完了',
        message: `${targetYear}年${targetMonth}月（${daysInMonth}日分）のシフトを生成しました`,
        color: 'green'
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'シフト生成に失敗しました';
      setError(errorMessage);
      
      notifications.show({
        title: 'エラー',
        message: errorMessage,
        color: 'red'
      });
    } finally {
      setIsGeneratingSchedule(false);
    }
  };

  // 日付別にシフトをグループ化
  const groupedAssignments = assignments.reduce((acc, assignment) => {
    const date = assignment.date;
    if (!acc[date]) {
      acc[date] = {};
    }
    acc[date][assignment.shift] = assignment;
    return acc;
  }, {} as Record<string, Record<string, Assignment>>);

  const sortedDates = Object.keys(groupedAssignments).sort();
  
  // 従業員ID から苗字を取得するヘルパー関数
  const getEmployeeSurname = (employeeId: string) => {
    const employee = employees.find(emp => emp.id === employeeId);
    if (!employee) return '-';
    
    // 生成される苗字は全て2文字なので、最初の2文字を苗字として抽出
    return employee.name.substring(0, 2);
  };
  
  // ライン定義
  const lines = ['A', 'B', 'C', 'D', 'E'] as const;

  // ライン別表示コンポーネント
  const LineBasedView = () => (
    <ScrollArea type="auto">
      <Table striped highlightOnHover style={{ minWidth: '800px' }}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th 
              style={{ 
                minWidth: '180px',
                position: 'sticky',
                left: 0,
                backgroundColor: 'var(--mantine-color-body)',
                zIndex: 1,
                borderRight: '1px solid var(--mantine-color-gray-3)'
              }}
            >
              シフト / ライン
            </Table.Th>
            {sortedDates.map((date) => {
              const dateObj = new Date(date);
              const dayOfWeek = ['日', '月', '火', '水', '木', '金', '土'][dateObj.getDay()];
              const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
              
              return (
                <Table.Th 
                  key={date} 
                  style={{ textAlign: 'center', minWidth: '60px' }}
                >
                  <Stack gap={2} align="center">
                    <Text size="sm" fw={500}>
                      {dateObj.getDate()}日
                    </Text>
                    <Badge
                      size="xs"
                      color={isWeekend ? 'red' : 'gray'}
                      variant="light"
                    >
                      {dayOfWeek}
                    </Badge>
                  </Stack>
                </Table.Th>
              );
            })}
          </Table.Tr>
        </Table.Thead>
        
        <Table.Tbody>
          {/* 日勤シフト */}
          {/* 日勤 - 各ライン */}
          {lines.map((line) => (
            <Table.Tr key={`day-${line}`}>
              <Table.Td
                style={{
                  position: 'sticky',
                  left: 0,
                  backgroundColor: 'var(--mantine-color-body)',
                  zIndex: 1,
                  borderRight: '1px solid var(--mantine-color-gray-3)',
                  minWidth: '180px'
                }}
              >
                <Group gap="xs">
                  <Badge color="blue" variant="light" size="sm">
                    日勤
                  </Badge>
                  <Badge variant="outline" size="sm">
                    ライン {line}
                  </Badge>
                </Group>
              </Table.Td>
              {sortedDates.map((date) => {
                const dayAssignment = groupedAssignments[date]?.['DAY'];
                
                return (
                  <Table.Td key={`day-${line}-${date}`} style={{ textAlign: 'center' }}>
                    <Text size="sm">
                      {dayAssignment?.lines[line] ? getEmployeeSurname(dayAssignment.lines[line]) : '-'}
                    </Text>
                  </Table.Td>
                );
              })}
            </Table.Tr>
          ))}
          
          {/* 日勤 - 責任者 */}
          <Table.Tr style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
            <Table.Td
              style={{
                position: 'sticky',
                left: 0,
                backgroundColor: 'var(--mantine-color-blue-0)',
                zIndex: 1,
                borderRight: '1px solid var(--mantine-color-gray-3)',
                minHeight: '44px',
                minWidth: '180px'
              }}
            >
              <Group gap="xs" style={{ minHeight: '24px' }}>
                <Badge color="blue" variant="light" size="sm">
                  日勤
                </Badge>
                <Badge color="blue" variant="filled" size="sm">
                  責任者
                </Badge>
              </Group>
            </Table.Td>
            {sortedDates.map((date) => {
              const dayAssignment = groupedAssignments[date]?.['DAY'];
              
              return (
                <Table.Td key={`day-supervisor-${date}`} style={{ textAlign: 'center' }}>
                  <Text size="sm" fw={500} c="blue">
                    {dayAssignment?.supervisorId ? getEmployeeSurname(dayAssignment.supervisorId) : '-'}
                  </Text>
                </Table.Td>
              );
            })}
          </Table.Tr>
          
          {/* 遅番シフト */}
          {/* 遅番 - 各ライン */}
          {lines.map((line) => (
            <Table.Tr key={`late-${line}`}>
              <Table.Td
                style={{
                  position: 'sticky',
                  left: 0,
                  backgroundColor: 'var(--mantine-color-body)',
                  zIndex: 1,
                  borderRight: '1px solid var(--mantine-color-gray-3)',
                  minWidth: '180px'
                }}
              >
                <Group gap="xs">
                  <Badge color="orange" variant="light" size="sm">
                    遅番
                  </Badge>
                  <Badge variant="outline" size="sm">
                    ライン {line}
                  </Badge>
                </Group>
              </Table.Td>
              {sortedDates.map((date) => {
                const lateAssignment = groupedAssignments[date]?.['LATE'];
                
                return (
                  <Table.Td key={`late-${line}-${date}`} style={{ textAlign: 'center' }}>
                    <Text size="sm">
                      {lateAssignment?.lines[line] ? getEmployeeSurname(lateAssignment.lines[line]) : '-'}
                    </Text>
                  </Table.Td>
                );
              })}
            </Table.Tr>
          ))}
          
          {/* 遅番 - 責任者 */}
          <Table.Tr style={{ backgroundColor: 'var(--mantine-color-orange-0)' }}>
            <Table.Td
              style={{
                position: 'sticky',
                left: 0,
                backgroundColor: 'var(--mantine-color-orange-0)',
                zIndex: 1,
                borderRight: '1px solid var(--mantine-color-gray-3)',
                minHeight: '44px',
                minWidth: '180px'
              }}
            >
              <Group gap="xs" style={{ minHeight: '24px' }}>
                <Badge color="orange" variant="light" size="sm">
                  遅番
                </Badge>
                <Badge color="orange" variant="filled" size="sm">
                  責任者
                </Badge>
              </Group>
            </Table.Td>
            {sortedDates.map((date) => {
              const lateAssignment = groupedAssignments[date]?.['LATE'];
              
              return (
                <Table.Td key={`late-supervisor-${date}`} style={{ textAlign: 'center' }}>
                  <Text size="sm" fw={500} c="orange">
                    {lateAssignment?.supervisorId ? getEmployeeSurname(lateAssignment.supervisorId) : '-'}
                  </Text>
                </Table.Td>
              );
            })}
          </Table.Tr>
        </Table.Tbody>
      </Table>
    </ScrollArea>
  );

  // 従業員別表示コンポーネント
  const EmployeeBasedView = () => (
    <ScrollArea type="auto">
      <Table striped highlightOnHover style={{ minWidth: '800px' }}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th 
              style={{ 
                minWidth: '150px',
                position: 'sticky',
                left: 0,
                backgroundColor: 'var(--mantine-color-body)',
                zIndex: 1,
                borderRight: '1px solid var(--mantine-color-gray-3)'
              }}
            >
              従業員
            </Table.Th>
            {sortedDates.map((date) => {
              const dateObj = new Date(date);
              const dayOfWeek = ['日', '月', '火', '水', '木', '金', '土'][dateObj.getDay()];
              const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
              
              return (
                <Table.Th 
                  key={date} 
                  style={{ textAlign: 'center', minWidth: '80px' }}
                >
                  <Stack gap={2} align="center">
                    <Text size="sm" fw={500}>
                      {dateObj.getDate()}日
                    </Text>
                    <Badge
                      size="xs"
                      color={isWeekend ? 'red' : 'gray'}
                      variant="light"
                    >
                      {dayOfWeek}
                    </Badge>
                  </Stack>
                </Table.Th>
              );
            })}
          </Table.Tr>
        </Table.Thead>
        
        <Table.Tbody>
          {employees.map((employee) => (
            <Table.Tr key={employee.id}>
              <Table.Td
                style={{
                  position: 'sticky',
                  left: 0,
                  backgroundColor: 'var(--mantine-color-body)',
                  zIndex: 1,
                  borderRight: '1px solid var(--mantine-color-gray-3)',
                  minWidth: '150px'
                }}
              >
                <Stack gap={4}>
                  <Text size="sm" fw={500}>
                    {employee.name.substring(0, 2)}
                  </Text>
                  <Group gap={4}>
                    {employee.skills
                      .sort((a, b) => a.localeCompare(b))
                      .map((skill) => (
                        <Badge key={skill} size="xs" variant="outline">
                          {skill}
                        </Badge>
                      ))
                    }
                  </Group>
                </Stack>
              </Table.Td>
              {sortedDates.map((date) => {
                // その従業員がその日に配置されているシフト・ラインを検索
                const dayAssignment = groupedAssignments[date]?.['DAY'];
                const lateAssignment = groupedAssignments[date]?.['LATE'];
                
                let assignmentInfo = null;
                let isResponsible = false;
                
                // 日勤での配置をチェック
                if (dayAssignment) {
                  if (dayAssignment.supervisorId === employee.id) {
                    assignmentInfo = { shift: 'DAY', line: '責任者' };
                    isResponsible = true;
                  } else {
                    for (const [line, assignedId] of Object.entries(dayAssignment.lines)) {
                      if (assignedId === employee.id) {
                        assignmentInfo = { shift: 'DAY', line };
                        break;
                      }
                    }
                  }
                }
                
                // 遅番での配置をチェック（日勤で見つからなかった場合のみ）
                if (!assignmentInfo && lateAssignment) {
                  if (lateAssignment.supervisorId === employee.id) {
                    assignmentInfo = { shift: 'LATE', line: '責任者' };
                    isResponsible = true;
                  } else {
                    for (const [line, assignedId] of Object.entries(lateAssignment.lines)) {
                      if (assignedId === employee.id) {
                        assignmentInfo = { shift: 'LATE', line };
                        break;
                      }
                    }
                  }
                }
                
                return (
                  <Table.Td key={`${employee.id}-${date}`} style={{ textAlign: 'center' }}>
                    {assignmentInfo ? (
                      <Stack gap={2} align="center">
                        <Badge 
                          color={assignmentInfo.shift === 'DAY' ? 'blue' : 'orange'} 
                          variant="light" 
                          size="xs"
                        >
                          {assignmentInfo.shift === 'DAY' ? '日勤' : '遅番'}
                        </Badge>
                        <Badge 
                          color={isResponsible ? (assignmentInfo.shift === 'DAY' ? 'blue' : 'orange') : 'gray'}
                          variant={isResponsible ? 'filled' : 'outline'}
                          size="xs"
                        >
                          {assignmentInfo.line === '責任者' ? '責任者' : `ライン${assignmentInfo.line}`}
                        </Badge>
                      </Stack>
                    ) : (
                      <Text size="sm" c="dimmed">休</Text>
                    )}
                  </Table.Td>
                );
              })}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </ScrollArea>
  );

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={3} size="h4">
            <Group gap="xs">
              <IconCalendarStats size={20} />
              シフト一覧 (1ヶ月分)
            </Group>
          </Title>
          <Button
            onClick={generateSchedule}
            loading={isGeneratingSchedule}
            leftSection={<IconPlaylist size={16} />}
            disabled={!canGenerateSchedule()}
          >
            シフト生成
          </Button>
        </Group>

        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="エラー"
            color="red"
            onClose={clearError}
            withCloseButton
          >
            {error}
          </Alert>
        )}

        <div style={{ position: 'relative' }}>
          <LoadingOverlay visible={isGeneratingSchedule} />
          
          {assignments.length === 0 && !isGeneratingSchedule ? (
            <Text c="dimmed" ta="center" py="xl">
              {employees.length === 0 ? (
                <>
                  「従業員生成」ボタンをクリックして従業員を生成してください
                  <br />
                  <Text size="sm" c="dimmed">
                    従業員生成後にシフト作成が可能になります
                  </Text>
                </>
              ) : (
                <>
                  「シフト生成」ボタンをクリックしてシフト表を生成してください
                  <br />
                  <Text size="sm" c="dimmed">
                    対象: {targetYear}年{targetMonth}月 | 従業員: {employees.length}名
                  </Text>
                </>
              )}
            </Text>
          ) : (
            <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'line')}>
              <Tabs.List>
                <Tabs.Tab value="line" leftSection={<IconBuilding size={16} />}>
                  ライン別
                </Tabs.Tab>
                <Tabs.Tab value="employee" leftSection={<IconUsers size={16} />}>
                  従業員別
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="line" pt="md">
                <LineBasedView />
              </Tabs.Panel>

              <Tabs.Panel value="employee" pt="md">
                <EmployeeBasedView />
              </Tabs.Panel>
            </Tabs>
          )}
        </div>

        {assignments.length > 0 && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              表示期間: {targetYear}年{targetMonth}月 ({sortedDates.length}日間)
            </Text>
            <Text size="sm" c="dimmed">
              総シフト数: {assignments.length}シフト
            </Text>
          </Group>
        )}
      </Stack>
    </Paper>
  );
}